/** Pure helpers for the Lists home (Slice 8): list-type classification, the search/sort/type-filter
 * pipeline, and the localStorage "recently viewed" ring. Extracted from the component so the
 * ordering/filtering logic is unit-tested independently of React. */
import type { CrmListSummary } from '../../types';

/**
 * `crm_list.list_kind` of the imported IOT deal pipeline — the one list the firm means by "all
 * deals". Same string the backend keys on (`legacy_census.DEAL_PIPELINE_KIND`; it is also the
 * `list_kind` default of `GET /api/crm/board-v2`). List IDs differ per environment (prod's IOT is
 * 1, dev's is not), so always resolve by KIND, never by a hardcoded id.
 */
export const DEAL_PIPELINE_LIST_KIND = 'deal_pipeline';

/**
 * The deal-pipeline list among the caller's entitled lists, or null when there isn't one visible.
 * Lowest id wins if several exist — the same tie-break the server applies (board-v2's
 * `order_by(crm_list.c.id)` in `backend/app/routers/crm.py`).
 */
export function findDealPipelineList<T extends { id: number; list_kind: string }>(
  lists: readonly T[] | undefined | null,
): T | null {
  const matches = (lists ?? []).filter((l) => l.list_kind === DEAL_PIPELINE_LIST_KIND);
  if (matches.length === 0) return null;
  return matches.reduce((lowest, l) => (l.id < lowest.id ? l : lowest));
}

export type ListType = 'person' | 'organization' | 'opportunity';
export type ListSort = 'recent' | 'name' | 'entries';
export type ListTypeFilter = 'all' | ListType;

/** A list's type for the filter chips: person / organization membership lists by their subject kind;
 * everything else (scratch opportunity lists AND the Affinity `deal_pipeline` IOT) = opportunity. */
export function listTypeOf(l: Pick<CrmListSummary, 'entry_subject_kind'>): ListType {
  if (l.entry_subject_kind === 'person') return 'person';
  if (l.entry_subject_kind === 'organization') return 'organization';
  return 'opportunity';
}

const RECENTS_KEY = 'crm.recentLists';
const RECENTS_CAP = 24;

/** The recently-viewed list ids, most-recent first. Tolerant of missing/corrupt storage + SSR. */
export function readRecentLists(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is number => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

/** Move `id` to the front of the recently-viewed ring (dedup, capped). Returns the new list. */
export function pushRecentList(id: number): number[] {
  const next = [id, ...readRecentLists().filter((x) => x !== id)].slice(0, RECENTS_CAP);
  if (typeof window !== 'undefined') {
    try { window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { /* quota / private mode */ }
  }
  return next;
}

interface Opts {
  search: string;
  sort: ListSort;
  typeFilter: ListTypeFilter;
  recentIds: number[];
}

/** Filter by name substring + type chip, then order by the chosen sort. `recent` puts
 * recently-viewed lists first (in visit order), remaining lists after by name — so a fresh session
 * with no recents still shows a stable A→Z list rather than an arbitrary order. */
export function sortAndFilterLists(lists: CrmListSummary[], { search, sort, typeFilter, recentIds }: Opts): CrmListSummary[] {
  const needle = search.trim().toLowerCase();
  const filtered = lists.filter((l) => {
    if (typeFilter !== 'all' && listTypeOf(l) !== typeFilter) return false;
    return !needle || l.name.toLowerCase().includes(needle);
  });
  const byName = (a: CrmListSummary, b: CrmListSummary) => a.name.localeCompare(b.name);
  if (sort === 'name') return [...filtered].sort(byName);
  if (sort === 'entries') return [...filtered].sort((a, b) => b.entry_count - a.entry_count || byName(a, b));
  // 'recent': recentIds order first, then the rest A→Z
  const rank = new Map(recentIds.map((id, i) => [id, i]));
  return [...filtered].sort((a, b) => {
    const ra = rank.has(a.id) ? rank.get(a.id)! : Infinity;
    const rb = rank.has(b.id) ? rank.get(b.id)! : Infinity;
    return ra !== rb ? ra - rb : byName(a, b);
  });
}
